/**
 * STEP 2-P7-M · Operator Order Detail (minimal shell).
 * STEP 2-P7-AK · Ship-only mutation added (REUSES existing backend
 *   POST /api/operator/orders/{sid}/ship — no new API, no schema
 *   change). Pick / Inspect / Pack remain READ-ONLY on this screen
 *   (their queues live in OperatorDashboard).
 *
 * REUSES existing backend:
 *   GET  /api/operator/orders/{seller_order_id}
 *   GET  /api/operator/orders/{seller_order_id}/pick-allocation
 *   POST /api/operator/orders/{seller_order_id}/ship
 * PII masking is inherited from `_scrub_order_for_operator` (backend).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Truck, Loader2, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
// STEP 3-OPS-C · REUSE existing trackingUrl helper (LOCK · no external API · official carrier URLs only).
// STEP 3-OPS-C-HOTFIX · use internal-only label (Carrier 실시간 상태 흉내 금지).
import { getTrackingAvailable, getTrackingUrl, getInternalShipmentLabel } from '@/lib/trackingUrl';
import { useState as _useState, useEffect as _useEffect } from 'react';

// STEP 3-WMS-B PHASE 2-B · Combined shipment UI (Operator-only · backend LOCK).
// Maps backend errors to Korean-safe toast.
const _mapErr = (e) => {
  const s = e?.response?.status; const d = e?.response?.data?.detail;
  if (s === 403) return '이 작업을 처리할 권한이 없습니다.';
  if (s === 404) return '대상을 찾을 수 없습니다.';
  if (s === 409) return typeof d === 'string' ? d : (d?.detail || '재시도 필요 · 일부 처리 실패');
  if (s === 400 && typeof d === 'string') {
    if (d.includes('이미')) return '이미 다른 합포 그룹에 포함된 주문이 있습니다.';
    if (d.includes('PACK')) return 'Pack 완료 상태가 아닌 주문이 있습니다.';
    if (d.includes('수령인')) return '수령인/배송지가 일치하지 않는 주문이 있습니다.';
    if (d.includes('송장')) return '대표 송장이 등록되지 않았습니다.';
    return d;
  }
  return typeof d === 'string' ? d : '요청 실패';
};

function CombinedCandidatesPanel({ sellerId, onConfirmed }) {
  const [groups, setGroups] = _useState([]);
  const [open, setOpen] = _useState(null); // selected group
  const [busy, setBusy] = _useState(false);
  const load = async () => {
    if (!sellerId) return;
    try {
      const r = await api.get('/operator/combined-shipments/candidates', { params: { seller_id: sellerId } });
      setGroups(r.data.items || []);
    } catch (e) { /* silent · candidates are optional hint */ }
  };
  _useEffect(() => { load(); }, [sellerId]);
  if (!groups.length) return null;
  const confirm = async (g) => {
    setBusy(true);
    try {
      const client_ref = (crypto?.randomUUID?.() || `cr-${Date.now()}-${Math.random().toString(36).slice(2,10)}`);
      await api.post('/operator/combined-shipments', {
        seller_id: sellerId,
        seller_order_ids: g.orders.map((o) => o.seller_order_id),
        client_ref,
      });
      toast.success('📦 합포 확정 완료');
      setOpen(null);
      await load();
      onConfirmed?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded bg-amber-50 border border-amber-200 p-2 text-xs" data-testid="operator-combined-candidates">
      <p className="font-bold text-amber-800 mb-1">📦 합포 가능 {groups.length}건</p>
      {groups.map((g, i) => (
        <button key={i} onClick={() => setOpen(g)}
                 className="block w-full text-left underline underline-offset-2 text-amber-900 hover:text-amber-700 py-0.5"
                 data-testid={`operator-combined-candidate-${i}`}>
          → {g.orders[0].recipient_name} · {g.orders.length}건 합포하기
        </button>
      ))}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setOpen(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-4 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="operator-combined-confirm-modal">
            <h3 className="font-bold">📦 합포 확정</h3>
            <div className="text-xs bg-neutral-50 p-2 rounded">
              <p><b>수령인:</b> {open.orders[0].recipient_name}</p>
              <p><b>전화:</b> {open.orders[0].phone}</p>
            </div>
            <div className="text-xs space-y-1">
              <p className="font-bold">포함 주문 {open.orders.length}건</p>
              {open.orders.map((o) => (
                <div key={o.seller_order_id} className="border rounded px-2 py-1">
                  <p className="font-mono">{o.order_number || o.seller_order_id}</p>
                  <p className="text-neutral-500">상품 {o.items_count}종</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpen(null)} disabled={busy}
                       className="px-3 py-1.5 rounded border text-sm disabled:opacity-50">취소</button>
              <button onClick={() => confirm(open)} disabled={busy}
                       className="px-3 py-1.5 rounded bg-[#FF3B30] text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                       data-testid="operator-combined-confirm-btn">
                {busy && <Loader2 size={12} className="animate-spin" />}
                📦 합포 확정
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CombinedGroupPanel({ cid, onDone }) {
  const [c, setC] = _useState(null);
  const [form, setForm] = _useState({ courier: 'CJ대한통운', tracking_number: '' });
  const [busy, setBusy] = _useState(false);
  const [shipConfirm, setShipConfirm] = _useState(false);
  const [deliverConfirm, setDeliverConfirm] = _useState(false);
  const load = async () => {
    try { const r = await api.get(`/operator/combined-shipments/${cid}`); setC(r.data); }
    catch (e) { toast.error(_mapErr(e)); }
  };
  _useEffect(() => { load(); }, [cid]);
  if (!c) return <div className="mt-3 text-xs text-neutral-500">합포 그룹 로딩...</div>;
  const sh = c.shipment || {};
  const hasTracking = Boolean(sh.tracking_number);
  const shippedAlready = c.status === 'SHIPPED';
  const deliveredAlready = c.status === 'DELIVERED';
  const submitTracking = async () => {
    if (!form.courier || !form.tracking_number) return toast.error('택배사와 송장번호가 필요합니다.');
    setBusy(true);
    try {
      await api.post(`/operator/combined-shipments/${cid}/tracking`, {
        courier: form.courier, tracking_number: form.tracking_number.trim(),
        shipping_mode: 'INTEGRATED',
      });
      toast.success('대표 송장 등록 완료');
      await load();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };
  const doShip = async () => {
    setBusy(true);
    try {
      await api.post(`/operator/combined-shipments/${cid}/ship`);
      toast.success('📦 합포 출고 완료');
      setShipConfirm(false);
      await load();
      onDone?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };
  const doDeliver = async () => {
    setBusy(true);
    try {
      await api.post(`/operator/combined-shipments/${cid}/delivered`);
      toast.success('✅ 합포 배송완료 처리 완료');
      setDeliverConfirm(false);
      await load();
      onDone?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };
  // STEP 3-WMS-B PHASE 5-HOTFIX · Operator Remove / Ungroup UI wiring.
  //   Reuses PHASE 5 endpoints (DELETE members/{sid} · POST ungroup).
  //   Guards mirror backend: only DRAFT/CONFIRMED · no tracking · not shipped/delivered/cancelled.
  const canModify = !hasTracking && !shippedAlready && !deliveredAlready && c.status !== 'CANCELLED';
  const doRemoveMember = async (sid) => {
    if (!window.confirm(`주문 ${sid} 을 합포에서 제외합니다. 계속하시겠습니까?`)) return;
    setBusy(true);
    try {
      await api.delete(`/operator/combined-shipments/${cid}/members/${sid}`);
      toast.success('합포에서 제외되었습니다.');
      await load(); onDone?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };
  const doUngroup = async () => {
    if (!window.confirm('합포를 전체 해제합니다. 계속하시겠습니까?')) return;
    setBusy(true);
    try {
      await api.post(`/operator/combined-shipments/${cid}/ungroup`);
      toast.success('합포가 해제되었습니다.');
      await load(); onDone?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };
  const trackAvailable = hasTracking && getTrackingAvailable(sh.courier, sh.tracking_number);
  const trackUrl = trackAvailable ? getTrackingUrl(sh.courier, sh.tracking_number) : null;
  return (
    <div className="mt-3 rounded bg-amber-50 border border-amber-200 p-3 space-y-2 text-xs"
          data-testid="operator-detail-combined-panel">
      <div className="flex items-center justify-between">
        <p className="font-bold text-amber-800">📦 합포배송 그룹 · <span className="font-mono">{cid}</span></p>
        <span className="px-1.5 py-0.5 rounded-full bg-white border text-[10px] font-bold">{c.status}</span>
      </div>
      <div className="border-t border-amber-200 pt-1">
        <p className="font-bold">포함 주문 {(c.members || []).length}건</p>
        {(c.members || []).map((m) => (
          <div key={m.seller_order_id} className="flex items-center justify-between mt-0.5" data-testid={`operator-combined-member-${m.seller_order_id}`}>
            <p className="font-mono text-[11px]">· {m.seller_order_id}</p>
            {canModify && (
              <button onClick={() => doRemoveMember(m.seller_order_id)} disabled={busy}
                       className="text-[10px] px-1.5 py-0.5 rounded border border-red-300 text-red-700 disabled:opacity-50"
                       data-testid={`operator-combined-remove-${m.seller_order_id}`}>합포에서 제외</button>
            )}
          </div>
        ))}
        {canModify && (
          <button onClick={doUngroup} disabled={busy}
                   className="mt-2 w-full px-2 py-1 rounded border border-red-300 text-red-700 text-[11px] font-bold disabled:opacity-50"
                   data-testid="operator-combined-ungroup-btn">🔓 합포 전체 해제</button>
        )}
        {!canModify && (hasTracking || shippedAlready || deliveredAlready) && (
          <p className="text-[10px] text-neutral-500 mt-1">송장 등록 후에는 합포를 변경할 수 없습니다.</p>
        )}
      </div>
      {hasTracking ? (
        <div className="border-t border-amber-200 pt-1">
          <p><b>택배사:</b> {sh.courier}</p>
          <p><b>송장번호:</b> <span className="font-mono">{sh.tracking_number}</span></p>
          {trackAvailable && trackUrl && (
            <a href={trackUrl} target="_blank" rel="noreferrer noopener"
                className="inline-block mt-1 font-bold text-[#FF3B30] underline underline-offset-2"
                data-testid="operator-combined-track-btn">배송조회 ↗</a>
          )}
        </div>
      ) : (
        <div className="border-t border-amber-200 pt-1 space-y-1">
          <p className="font-bold">대표 송장 입력</p>
          <div className="flex gap-1">
            <input value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })}
                    placeholder="택배사" disabled={busy}
                    className="flex-1 border rounded px-2 py-1"
                    data-testid="operator-combined-tracking-courier" />
            <input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                    placeholder="송장번호" disabled={busy}
                    className="flex-1 border rounded px-2 py-1 font-mono"
                    data-testid="operator-combined-tracking-number" />
          </div>
          <button onClick={submitTracking} disabled={busy || !form.tracking_number}
                   className="px-3 py-1 rounded bg-[#FF3B30] text-white inline-flex items-center gap-1 disabled:opacity-50"
                   data-testid="operator-combined-tracking-submit">
            {busy && <Loader2 size={10} className="animate-spin" />} 저장
          </button>
        </div>
      )}
      {!shippedAlready && hasTracking && (
        <button onClick={() => setShipConfirm(true)} disabled={busy}
                 className="w-full px-3 py-1.5 rounded bg-[#FF3B30] text-white font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50"
                 data-testid="operator-combined-ship-btn">
          📦 합포 출고
        </button>
      )}
      {shippedAlready && (
        <div className="border-t border-amber-200 pt-2 space-y-2">
          <p className="text-emerald-700 font-bold">출고 완료</p>
          <button onClick={() => setDeliverConfirm(true)} disabled={busy}
                   className="w-full px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50"
                   data-testid="operator-combined-deliver-btn">
            ✅ 배송완료 처리
          </button>
        </div>
      )}
      {deliveredAlready && <p className="text-emerald-800 font-bold" data-testid="operator-combined-delivered-label">✅ 배송완료</p>}
      {deliverConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setDeliverConfirm(false)}>
          <div className="bg-white rounded-lg max-w-sm w-full p-4 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="operator-combined-deliver-confirm">
            <h3 className="font-bold">✅ 합포 배송완료 처리</h3>
            <p className="text-sm">합포 배송의 모든 주문을 배송완료 처리합니다.</p>
            <ul className="text-xs font-mono bg-neutral-50 rounded p-2">
              {(c.members || []).map((m) => <li key={m.seller_order_id}>· {m.seller_order_id}</li>)}
            </ul>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeliverConfirm(false)} disabled={busy}
                       className="px-3 py-1.5 rounded border text-sm disabled:opacity-50">취소</button>
              <button onClick={doDeliver} disabled={busy}
                       className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                       data-testid="operator-combined-deliver-confirm-btn">
                {busy && <Loader2 size={12} className="animate-spin" />} 배송완료 처리
              </button>
            </div>
          </div>
        </div>
      )}
      {shipConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setShipConfirm(false)}>
          <div className="bg-white rounded-lg max-w-sm w-full p-4 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="operator-combined-ship-confirm">
            <h3 className="font-bold">📦 합포 출고</h3>
            <p className="text-sm">{(c.members || []).length}개 주문을 하나의 배송으로 출고합니다.</p>
            <ul className="text-xs font-mono bg-neutral-50 rounded p-2">
              {(c.members || []).map((m) => <li key={m.seller_order_id}>· {m.seller_order_id}</li>)}
            </ul>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShipConfirm(false)} disabled={busy}
                       className="px-3 py-1.5 rounded border text-sm disabled:opacity-50">취소</button>
              <button onClick={doShip} disabled={busy}
                       className="px-3 py-1.5 rounded bg-[#FF3B30] text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                       data-testid="operator-combined-ship-confirm-btn">
                {busy && <Loader2 size={12} className="animate-spin" />} 합포 출고
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL = {
  pending:   '대기',
  picking:   '피킹',
  picked:    '피킹 완료',
  inspected: '검수 완료',
  packing:   '포장',
  packed:    '포장 완료',
  shipped:   '출고',
};

export default function OperatorOrderDetail() {
  const { sid } = useParams();
  const [order, setOrder]   = useState(null);
  const [alloc, setAlloc]   = useState(null);
  const [err, setErr]       = useState('');
  const [loading, setLoad]  = useState(true);
  const [shipping, setShipping] = useState(false);

  const load = React.useCallback(async () => {
    try {
      setLoad(true); setErr('');
      const [r1, r2] = await Promise.all([
        api.get(`/operator/orders/${sid}`),
        api.get(`/operator/orders/${sid}/pick-allocation`).catch(() => ({ data: null })),
      ]);
      setOrder(r1.data);
      setAlloc(r2.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || '주문 상세를 불러오지 못했습니다.');
    } finally {
      setLoad(false);
    }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  // STEP 2-P7-AK · Ship handler — REUSES existing POST /operator/orders/
  // {sid}/ship. Backend enforces all guards (inspected + PACKED + tracking).
  // Inventory hooks (P7-V) fire server-side; frontend just refetches on
  // success and surfaces the backend detail verbatim on failure.
  const doShip = async () => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm('출고 완료 처리하시겠습니까?\n출고 처리 시 재고가 실제 출고 상태로 반영됩니다.');
    if (!ok) return;
    setShipping(true);
    try {
      await api.post(`/operator/orders/${sid}/ship`);
      toast.success('출고 완료 처리했습니다.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '출고 처리 실패');
    } finally {
      setShipping(false);
    }
  };

  // Ship button visibility mirrors backend `operator_ship` guards exactly:
  //   fulfillment.status == "inspected"
  //   AND (packing_status == "PACKED" OR packing_status == null) — legacy
  //   AND shipment.tracking_number present
  const canShip = order
    && order.fulfillment_status === 'inspected'
    && (order.packing_status === 'PACKED' || order.packing_status == null)
    && !!(order.shipment && order.shipment.tracking_number);

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white" data-testid="operator-order-detail">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between">
          <Link to="/operator?section=orders" className="text-xs font-bold text-[#FF3B30]" data-testid="operator-order-detail-back">
            ← 주문 작업으로
          </Link>
          <span className="text-[10px] text-gray-400 font-mono">{sid}</span>
        </div>

        {loading && <div className="p-6 text-sm text-gray-400" data-testid="operator-order-detail-loading">불러오는 중…</div>}

        {err && (
          <div className="p-6 rounded-2xl border border-red-500/30 bg-red-500/5 text-sm text-red-500" data-testid="operator-order-detail-error">
            {err}
          </div>
        )}

        {order && !err && (
          <>
            {/* Header */}
            <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-black tracking-widest uppercase text-gray-400">주문번호</p>
                  <h1 className="text-2xl font-black mt-1" data-testid="operator-order-detail-number">
                    {order.order_number || sid}
                  </h1>
                  <p className="text-[11px] text-gray-500 mt-1 font-mono">seller_order_id · {sid}</p>
                </div>
                <div className="text-right space-y-1">
                  <span className="inline-block px-2.5 py-1 rounded-full text-[11px] font-bold bg-black text-white dark:bg-white dark:text-black" data-testid="operator-order-detail-status">
                    {order.status || '-'}
                  </span>
                  <div>
                    <span className="inline-block px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" data-testid="operator-order-detail-fulfillment-status">
                      {STATUS_LABEL[(order.fulfillment?.status) || 'pending'] || order.fulfillment?.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Assignment scope */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <InfoCard label="판매자"   value={order.seller_id || '-'} testid="operator-detail-seller" />
              <InfoCard label="Warehouse" value={order.assignment?.warehouse_id || '-'} testid="operator-detail-warehouse" />
              <InfoCard label="Operator"  value={order.assignment?.operator_id || '-'} testid="operator-detail-operator" />
            </div>

            {/* Customer / shipping (backend masking already applied) */}
            <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4" data-testid="operator-detail-shipping">
              <p className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-2">배송 정보</p>
              <div className="text-sm space-y-1">
                <div>{order.shipping_address_snapshot?.recipient_name || '-'} · {order.shipping_address_snapshot?.phone || '-'}</div>
                <div className="text-gray-500">
                  ({order.shipping_address_snapshot?.postal_code || '-'}) {order.shipping_address_snapshot?.address1 || '-'} {order.shipping_address_snapshot?.address2 || ''}
                </div>
                {order.shipping_address_snapshot?.delivery_memo && (
                  <div className="text-xs text-gray-500 mt-1">💬 {order.shipping_address_snapshot.delivery_memo}</div>
                )}
              </div>
            </div>

            {/* Items */}
            <div className="rounded-2xl border border-black/10 dark:border-white/10 overflow-hidden" data-testid="operator-detail-items">
              <div className="px-4 py-3 border-b border-black/10 dark:border-white/10">
                <p className="text-[10px] font-black tracking-widest uppercase text-gray-400">주문 상품 · {order.items?.length || 0}종</p>
              </div>
              <div className="divide-y divide-black/10 dark:divide-white/10">
                {(order.items || []).map((it, idx) => (
                  <div key={idx} className="px-4 py-3 grid grid-cols-12 gap-2 items-center text-sm" data-testid={`operator-detail-item-${idx}`}>
                    <div className="col-span-6">
                      <div className="font-medium truncate">{it.name_snapshot || it.product_id}</div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {it.option ? Object.entries(it.option).map(([k, v]) => `${k}: ${v}`).join(' · ') : '옵션 없음'}
                      </div>
                    </div>
                    <div className="col-span-2 text-xs font-mono text-gray-500 truncate">SKU {it.sku || '-'}</div>
                    <div className="col-span-2 text-xs font-mono text-gray-500 truncate">BC {it.barcode || '-'}</div>
                    <div className="col-span-2 text-right font-bold">{it.quantity || 1}개</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pick allocation (READ-ONLY) */}
            {alloc && Array.isArray(alloc.items) && alloc.items.length > 0 && (
              <div className="rounded-2xl border border-black/10 dark:border-white/10 overflow-hidden" data-testid="operator-detail-pick-allocation">
                <div className="px-4 py-3 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
                  <p className="text-[10px] font-black tracking-widest uppercase text-gray-400">피킹 위치 (READ-ONLY)</p>
                  <span className="text-[10px] text-gray-400">/operator?section=pick 에서 실제 피킹</span>
                </div>
                <div className="divide-y divide-black/10 dark:divide-white/10">
                  {alloc.items.map((row, idx) => (
                    <div key={idx} className="px-4 py-3" data-testid={`operator-detail-alloc-${idx}`}>
                      <div className="text-xs text-gray-500 mb-1">
                        {row.product_id} · {row.variant_key} · 필요 수량 {row.required_qty}
                      </div>
                      {row.location_managed && Array.isArray(row.allocations) ? (
                        <div className="flex flex-wrap gap-2">
                          {row.allocations.map((a, j) => (
                            <span key={j} className="inline-block px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs font-bold" data-testid={`operator-detail-alloc-loc-${idx}-${j}`}>
                              {a.code || a.location_id} · {a.quantity}개
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-amber-500">Location 미관리 · 미배정 재고</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shipment */}
            {order.shipment && (
              <div className="rounded-2xl border border-black/10 dark:border-white/10 p-4" data-testid="operator-detail-shipment">
                <p className="text-[10px] font-black tracking-widest uppercase text-gray-400 mb-2">배송/포장</p>
                <div className="text-sm grid grid-cols-2 gap-y-1">
                  <span className="text-gray-500">택배사</span>
                  <span data-testid="operator-detail-courier">{order.shipment.courier || '-'}</span>
                  <span className="text-gray-500">송장번호</span>
                  <span className="font-mono" data-testid="operator-detail-tracking">{order.shipment.tracking_number || '-'}</span>
                  <span className="text-gray-500">포장 상태</span>
                  <span data-testid="operator-detail-packing-status">{order.packing_status || '-'}</span>
                </div>
                {/* STEP 3-WMS-B · Combined shipment badge (physical group representative). */}
                {order.combined_shipment_id && (
                  <CombinedGroupPanel cid={order.combined_shipment_id} onDone={() => window.location.reload()} />
                )}
                {!order.combined_shipment_id && (
                  <CombinedCandidatesPanel sellerId={order.seller_id} onConfirmed={() => window.location.reload()} />
                )}
                {/* STEP 3-OPS-C · Carrier tracking (REUSE trackingUrl helper · official URL mapping only · no external API) */}
                {(order.shipment.courier || order.shipment.tracking_number) && (() => {
                  const sh = order.shipment || {};
                  const status = getInternalShipmentLabel({ shipment: sh, orderStatus: order.status, fulfillmentStatus: order.fulfillment_status });
                  const available = getTrackingAvailable(sh.courier, sh.tracking_number);
                  const url = available ? getTrackingUrl(sh.courier, sh.tracking_number) : null;
                  return (
                    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="operator-detail-tracking-row">
                      {status && <span className="text-[11px] px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold" data-testid="operator-detail-track-status">{status}</span>}
                      {available && url ? (
                        <a href={url} target="_blank" rel="noreferrer noopener"
                           className="inline-flex items-center gap-1 text-xs font-bold text-[#FF3B30] underline underline-offset-2 hover:text-[#e0332a]"
                           data-testid="operator-detail-track-btn">
                          <ExternalLink size={12}/> 배송조회 ↗
                        </a>
                      ) : sh.tracking_number ? (
                        <span className="text-[11px] text-gray-400" data-testid="operator-detail-track-pending">배송조회 준비중</span>
                      ) : null}
                    </div>
                  );
                })()}
                {/* STEP 2-P7-AK · Ship completion button (backend guards enforced) */}
                {canShip && (
                  <button
                    onClick={doShip}
                    disabled={shipping}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#FF3B30] text-white text-sm font-black disabled:opacity-60 hover:opacity-95"
                    data-testid="operator-detail-ship-btn">
                    {shipping ? <Loader2 size={14} className="animate-spin"/> : <Truck size={14}/>}
                    {shipping ? '출고 처리 중…' : '출고 완료'}
                  </button>
                )}
                {order.fulfillment_status === 'shipped' && (
                  <p className="mt-3 text-xs font-black text-emerald-600 dark:text-emerald-400" data-testid="operator-detail-shipped-badge">
                    <Truck size={12} className="inline mr-1"/> 출고 완료 처리됨
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InfoCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3" data-testid={testid}>
      <p className="text-[10px] font-black tracking-widest uppercase text-gray-400">{label}</p>
      <p className="text-sm font-mono mt-1 truncate">{value}</p>
    </div>
  );
}
